import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import BackToTop from '@/components/base/BackToTop';
import Footer from '@/pages/home/components/Footer';
import WindHero from './components/WindHero';
import WindStats from './components/WindStats';
import WindBenefits from './components/WindBenefits';
import WindSolutions from './components/WindSolutions';
import WindProcess from './components/WindProcess';
import WindApplications from './components/WindApplications';
import WindFAQ from './components/WindFAQ';
import WindCTA from './components/WindCTA';

const PAGE_TITLE = 'Wind Energy Solutions Australia | Commercial Renewable Power — AK Energy';
const PAGE_DESCRIPTION = 'Discover commercial and industrial wind energy solutions across Australia. Find trusted wind farm developers, Corporate PPAs, small wind turbines, and hybrid microgrid providers on AK Energy.';

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Wind Energy Solutions Australia',
  description: PAGE_DESCRIPTION,
  url: 'https://ak-energyau.com/wind-energy-solutions',
  inLanguage: 'en-AU',
  isPartOf: {
    '@type': 'WebSite',
    name: 'AK Energy Australia',
    url: 'https://ak-energyau.com',
  },
  about: {
    '@type': 'Thing',
    name: 'Wind Energy',
    description: 'Renewable energy generated from wind using turbines to convert kinetic energy into electricity.',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://ak-energyau.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Wind Energy Solutions',
        item: 'https://ak-energyau.com/wind-energy-solutions',
      },
    ],
  },
  relatedLink: [
    'https://ak-energyau.com/solar-energy-australia',
  ],
};

export default function WindEnergyPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;

    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (metaDesc) metaDesc.content = PAGE_DESCRIPTION;

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = 'https://ak-energyau.com/wind-energy-solutions';

    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = 'Wind Energy Solutions Australia | AK Energy';

    const ogDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    if (ogDesc) ogDesc.content = PAGE_DESCRIPTION;

    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = 'https://ak-energyau.com/wind-energy-solutions';

    const twTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    if (twTitle) twTitle.content = 'Wind Energy Solutions Australia | AK Energy';

    const twDesc = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');
    if (twDesc) twDesc.content = PAGE_DESCRIPTION;

    let metaKw = document.querySelector<HTMLMetaElement>('meta[name="keywords"]');
    if (metaKw) {
      metaKw.content = 'wind energy Australia, wind farm Australia, commercial wind energy, wind power, corporate PPA Australia, small wind turbines, renewable energy Australia';
    }

    return () => {
      document.title = 'AK Energy Australia | Renewable Energy Solutions Marketplace';
    };
  }, []);

  return (
    <main className="relative bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />

      <Navbar />
      <WindHero />
      <WindStats />
      <WindBenefits />
      <WindSolutions />
      <WindProcess />
      <WindApplications />
      <WindFAQ />
      <WindCTA />
      <Footer />
      <BackToTop />
    </main>
  );
}